import React, {
  forwardRef, useEffect, useMemo, useState, useRef
} from 'react';
import { NavLink, useParams } from 'react-router-dom';
import 'moment-timezone';
import _ from 'lodash';
import MaterialTable, { MTableBody } from 'material-table';
import {
  Card, Form, Row, Col, Button,
} from 'react-bootstrap';
import Select from 'react-select';

import Delete from '@material-ui/icons/Delete';
import { History } from '@material-ui/icons';
import DownloadLink from '../../common/DownloadLink';
import PassportVersionHistoryModal from '../../../pages/project/PassportVersionHistoryModal';

import { years, tableIcons } from '../../../helpers';
import { getStickyColumns, localization, modifyDefaultOptions, styledCellSticky } from '../../../helpers/table';
import {FilterInput} from "../../../helpers/table.jsx"
import { status as PS, status_ru_extra, filterStatus, statusRuExtraWithPolicy } from '../../../helpers/passport';
import { getPathType } from '../../../helpers/typeProject';

import { getFooter } from '../helpers/ProjectRegisterTableHelper';

import { arrayStringRenderer, dateRenderer, floatRenderer } from '../../../services/formatSvc';
import {
  getStrategyPermissions,
  isAdmin,
  isAdminRead,
  isProjectAdmin,
  isProjectCreator, isStratAdmin,
} from '../../../services/rolesSvc';

import useAPI from '../../../hooks/useAPI';
import useUserContext from '../../../hooks/useUserContext';
import useToast from '../../../hooks/useToast';
import ConfirmModal from '../../common/ConfirmModal';
import FilterMultiSelect from '../../table/FilterMultiSelect';
import MTableFilterRow from '../../material-table-overrides/MTableFilterRow';
import Tooltip from '@mui/material/Tooltip';
import LoadingTable from '../../table/LoadingTable';
import StatusBadge from "../../common/StatusBadge";

const modifiedYears = [
  { value: null, label: 'Все периоды' },
  ...years.map((val) => ({ value: val, label: val }))];

export default function TabProjectRegister(props) {
  const api = useAPI();
  const toast = useToast();
  const defaultYear = 2025;
  const { stratId } = useParams();
  const MTableFiltersRef = useRef({});

  const [selYear, setSelYear] = useState({ value: defaultYear, label: defaultYear });
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const handleInputChange = async (val) => {
    setSelYear(val);
  };

  const isNew = selYear.value >= 2025;

  // #region Perms
  const userCtx = useUserContext();

  const canAddProjs = useMemo(() => isProjectCreator(userCtx, parseInt(stratId)), []);
  const canDeleteProj = (proj) => proj.status === PS.draft && proj.version === 1
      && (isProjectAdmin(userCtx, proj.originId) || isAdmin(userCtx));

  const canExportPassports = useMemo(
    () =>
    // выгрузку по ЦП проектов могут сделать:
    // ПО/админы
      isAdmin(userCtx)
        // руководители СП, администраторы СП
        || (userCtx.mine.strategies
          && userCtx.mine.strategies.find((p) => p.strategyId === parseInt(stratId) && (p.role === 'head' || p.role === 'admin'))),
    [],
  );

  const canViewReport = (projs) => {
    const stratProjIds = projs ? projs.map((p) => p.id) : [];
    // отчет по уникальным результатам могут видеть:
    // ПО/админы
    return isAdminRead(userCtx)
        // пользователи данного СП с ролями, позволяющими редактировать отчет проектов
        || getStrategyPermissions(userCtx, props.data.id).canWrite
        // пользователи проектов...
        || userCtx.perms.includes('projs')
        // ...данного СП с ролями, позволяющими редактировать отчет проектов
          && stratProjIds.find((id) => userCtx.mine.projs.find((p) => p.projId === id && ['admin', 'head'].includes(p.role)));
  };
  // #endregion

  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [projects, setProjects] = useState([]);
  const [isModalDeleteProj, setIsModalDeleteProj] = useState({ show: false, value: null });
  const [passportHistoryModal, setPassportHistoryModal] = useState(null);

  // фильтры по руководителям и мероприятиям
  const [headFilterLookup, setHeadFilterLookup] = useState({});
  const [letterFilterLookup, setLetterFilterLookup] = useState({});
  const [portfFilterLookup, setPortfFilterLookup] = useState({});

  useEffect(async () => {
    await getData(selYear.value); // первичный запрос данных с фильтром по году по умолчанию
    setIsLoaded(true)
  }, []);

  const getData = async (year, tags) => {
    try {
      const yearQuery = year ? `year=${year}&` : '';
      const tagsQuery = tags && tags.length  ? `labels=[${tags}]&` : '';
      const result = await api.get(`/projs?${yearQuery}${tagsQuery}strategyId=${props.data.id}&latest=true`);
      if(tagOptions.length === 0){
        setTagOptions([
          {label: 'Проекты без тега', value: -1},
          ...result.reduce((acc, project) => {
            const uniqueTags = []
            project.labels.forEach(tag => {
              if (!acc.some(opt => opt.value === tag.id)){
                uniqueTags.push({label: tag.name, value: tag.id })
              }
            })
            return [...acc, ...uniqueTags]
          }, [])
        ]);
      }
      // setIsLoaded(true)
      setProjects(result.map(el=>Object.assign(el,{headDisplayName:el?.head ? el.head.displayName : ''})));
      // фильтр из имеющихся руководителей
      setHeadFilterLookup(arrayToLookupSortAlph(result.filter((r) => r.head).map((r) => r.head), 'displayName', 'displayName'));

      // выбираем проекты, где есть letters (filter), берем только это поле (letters),
      // делаем из массивов в массиве один плоский массив (flat()), берем только уникальные значения (uniq),
      // и делаем из этого объект нужной структуры для фильтра
      // todo: нужно ли подключить полный справочник мероприятий?
      setLetterFilterLookup(Object.fromEntries(_.uniq(result.filter((r) => r.letters).map((r) => r.letters).flat()).map((l) => [l, l])));
      setPortfFilterLookup(arrayToLookup(result.map((r) => r.portfolio || {name: 'Без портфеля'}), 'name', 'name'));
    } catch (error) {
      setIsLoaded(true);
      setError(error);
    }
  };

  const handleDeleteProj = async () => {
    const result = await api.del(`/projs/${isModalDeleteProj.value}`, { id: isModalDeleteProj.value });
    if (result && !result.error) {
      toast.success('Проект успешно удален');
      setProjects((prev) => prev.filter((v) => v.id !== isModalDeleteProj.value));
    }
    setIsModalDeleteProj({ show: false, value: null });
  };

  // #region Lookups for filters
  /** Превращает массив объектов в объект формата { значение: заголовок },
   * который требуется для поля lookup в параметрах столбца
   */
  const arrayToLookup = (values, valueField, titleField) => (values && values.length > 0
    ? Object.fromEntries(values.map((v) => [v[valueField], v[titleField || valueField]]))
    : {});

  const arrayToLookupSortAlph = (values, valueField, titleField) => (values && values.length > 0
    ? Object.fromEntries(
      values
        .sort((a, b) => (a.displayName === b.displayName ? 0 : a.displayName < b.displayName ? -1 : 1))
        .map((v) => [v[valueField], v[titleField || valueField]]),
    )
    : {});

  // фильтр по соглашениям
  const [agreements, setAgreements] = useState({});
  useEffect(async () => {
    const agreements = await api.get('/mng/agreements');
    setAgreements(arrayToLookup(agreements, 'num'));
  }, []);

  // по кодам БК
  const [bcCodes, setBcCodes] = useState({});
  useEffect(async () => {
    const bcCodes = await api.get('/mng/bcCodes');
    setBcCodes(arrayToLookup(bcCodes, 'fullCode'));
  }, []);

  // по типу финансирования
  const [funding, setFunding] = useState({});
  useEffect(async () => {
    const funding = await api.get('/mng/funding');
    setFunding(arrayToLookup(funding, 'code'));
  }, []);

  // по типу проекта
  const [projTypes, setProjTypes] = useState({});
  useEffect(async () => {
    const projTypes = await api.get('/mng/proj/types');
    setProjTypes(arrayToLookup(projTypes, 'id', 'name'));
  }, []);
  
  const getCols = () => {
    const firstCols = [
      {
        title: 'Действ.',
        width: 115,
        cellStyle: styledCellSticky(),
        headerStyle: styledCellSticky(),
        filterCellStyle: styledCellSticky(),
        render: (rowData) =>
            <div style={{display: 'flex', gap: '10px'}}>
              <Tooltip title='История версий'>
                <History style={{ color: '#0D6EFD', cursor: 'pointer' }} onClick={() => {
                  setPassportHistoryModal({
                    id: rowData.id,
                    num: rowData.fullNumber,
                    version: rowData.version,
                    stratId: rowData.strategyId,
                  })
                }}/>
              </Tooltip>
              {
                (isProjectAdmin(userCtx, rowData.originId) || isAdmin(userCtx)) &&
                <Tooltip title='Удалить'>
                    <Delete style={{ color: canDeleteProj(rowData) ? '#dc3545' : 'rgba(0,0,0,0.28)' , cursor: canDeleteProj(rowData) ? 'pointer' : 'default'}} onClick={() => {
                      if (canDeleteProj(rowData)) {
                        setIsModalDeleteProj({
                          show: true,
                          value: rowData.id
                        });
                      }
                    }} />
                </Tooltip>
              }
              {rowData.labels.length > 0 && <Tooltip title={<div>
                {rowData.labels.map(label => <p>{label.name}</p>)}
              </div>}>
                <span style={{
                  color: '#0D6EFD',
                  fontSize: 18,
                  cursor: 'default'
                }}>#</span>
              </Tooltip>}
            </div>
      },
      { width: 80, title: <div style={{ width: '40px' }}>№</div>, field: 'fullNumber', filterComponent: ({ columnDef, onFilterChanged })=><FilterInput columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="fullNumber" />, },
      {
        title: 'Наименование проекта',
        field: 'name',
        width: 400,
        cellStyle: { minWidth: '400px' },
        render: (rowData) => <NavLink to={rowData.portfId ? `/${getPathType(rowData.strategy?.type)}/${stratId}/portfolios/${rowData.portfId}/projs/${rowData.id}` : `/${getPathType(rowData.strategy?.type)}/${stratId}/projs/${rowData.id}`}>{rowData.name}</NavLink>,
        filterComponent: ({ columnDef, onFilterChanged })=><FilterInput columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="name" />
      },
      { title: <div style={{ width: '80px' }}>Версия Цифрового паспорта</div>, field: 'version',
        width: 115,
        filterComponent: ({ columnDef, onFilterChanged })=><FilterInput columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="version" />, },
      {
        title: 'Руководитель проекта',
        field: 'headDisplayName',
        width: 200,
        cellStyle: { minWidth: '200px' },
        // render: (rowData) => (rowData.head ? rowData.head.displayName : ''),
        lookup: headFilterLookup,
        customFilterAndSearch: (term, rowData) => term.length === 0 || rowData.head && term.indexOf(rowData.head.displayName) >= 0,
        filterComponent: ({ columnDef, onFilterChanged })=><FilterMultiSelect columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="head" />
      },
      {
        title: 'Тип проекта',
        field: 'typeId',
        width: 200,
        cellStyle: { minWidth: '200px' },
        lookup: projTypes,
        filterComponent: ({ columnDef, onFilterChanged })=><FilterMultiSelect columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="typeId" />
      },
    ]
    const stickyColumns = getStickyColumns(firstCols,0)
    const allColumns = [
      ...stickyColumns,
    // {title: 'Уникальный результат', field: 'result', lookup: { 1:'Результат 1', 2:'Результат 2', 3:'Результат 3'}},
    {
      title: 'Бюджет из средств гранта (план) (руб.)',
        field: 'fbBudget',
      cellStyle: { minWidth: '200px' },
      render: (rowData) => floatRenderer(rowData.fbBudget),
        filterComponent: ({ columnDef, onFilterChanged })=><FilterInput columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="fbBudget" />
    },
    {
      title: 'Бюджет из средств софинансирования (план) (руб.)',
        field: 'sfBudget',
      cellStyle: { minWidth: '220px' },
      render: (rowData) => floatRenderer(rowData.sfBudget),
        filterComponent: ({ columnDef, onFilterChanged })=><FilterInput columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="sfBudget" />
    },
    {
      title: 'Соглашение',
        field: 'agreements',
      render: (rowData) => arrayStringRenderer(rowData.agreements),
      cellStyle: { minWidth: '200px' },
      lookup: agreements,
        customFilterAndSearch: (term, rowData) => term.length === 0 || rowData.agreements && _.intersection(term, rowData.agreements).length > 0,
      filterComponent: ({ columnDef, onFilterChanged })=><FilterMultiSelect columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="agreements" />
    },
    !(isNew && (props.data?.type === 'tech' || props.data?.type === 'goal')) && {
      title: 'Тип финансирования',
        field: 'funding',
      render: (rowData) => arrayStringRenderer([...new Set(rowData.funding.map((item) => (item === 'С' ? 'Специальная' : 'Базовая')))]),
      lookup: { С: 'Специальная', Б: 'Базовая' },
      customFilterAndSearch: (term, rowData) => term.length === 0 || rowData.funding && _.intersection(term, rowData.funding).length > 0,
        filterComponent: ({ columnDef, onFilterChanged })=><FilterMultiSelect columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="funding" />
    },
    {
      title: 'Код подраздела БК',
        field: 'bcCodes',
      cellStyle: { minWidth: '150px' },
      render: (rowData) => arrayStringRenderer(rowData.bcCodes),
        lookup: bcCodes,
      customFilterAndSearch: (term, rowData) => term.length === 0 || rowData.bcCodes && _.intersection(term, rowData.bcCodes).length > 0,
      filterComponent: ({ columnDef, onFilterChanged })=><FilterMultiSelect columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="bcCodes" />
    },
    // {title: 'Ключевой показатель проекта', field: 'mainProjIndicator', lookup: { 1:'Показатель 1', 2:'Показатель 2', 3:'Показатель 3'}},
    {
      title: 'Дата начала проекта',
        field: 'startDate',
      cellStyle: { minWidth: '120px' },
      render: (rowData) => dateRenderer(rowData.startDate),
        filterComponent: ({ columnDef, onFilterChanged })=><FilterInput columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="startDate" />

    },
    {
      title: 'Дата окончания проекта',
        field: 'endDate',
      cellStyle: { minWidth: '120px' },
      render: (rowData) => dateRenderer(rowData.endDate),
        filterComponent: ({ columnDef, onFilterChanged })=><FilterInput columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="endDate" />
    },
    // {title: 'Уникальный результат', field: 'result', lookup: { 1:'Результат 1', 2:'Результат 2', 3:'Результат 3'}},
    // {title: 'Бюджет из средств гранта (план) (руб.)',
    //   field: 'fbBudget',
    //   cellStyle: {minWidth: '200px'},
    //   render: rowData => floatRenderer(rowData.fbBudget),
    // },
    // {title: 'Бюджет из внебюджетных источников (план) (руб.)', field: 'sfBudget',
    //   cellStyle: {minWidth: '220px'},
    //   render: rowData => floatRenderer(rowData.sfBudget),
    // },
    {
      title: 'Мероприятие Постановления № 729',
        field: 'letters',
      cellStyle: { minWidth: '120px' },
      render: (rowData) => arrayStringRenderer(rowData.letters),
        lookup: letterFilterLookup,
      customFilterAndSearch: (term, rowData) => term.length === 0 || rowData.letters && _.intersection(term, rowData.letters).length > 0,
      filterComponent: ({ columnDef, onFilterChanged })=><FilterMultiSelect columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="letters" />
    },
    // {title: 'Соглашение', field: 'agreements',
    //   render: rowData => arrayStringRenderer(rowData.agreements),
    //   cellStyle: {minWidth: '200px'},
    //   lookup: agreements,
    //   customFilterAndSearch: (term, rowData) => term.length === 0 || rowData.agreements && _.intersection(term, rowData.agreements).length > 0},
    // {title: 'Тип финансирования', field: 'funding',
    //   render: rowData => arrayStringRenderer(rowData.funding.map(item=>item==='С'?'Специальная':'Базовая')),
    //   lookup: {'С':'Специальная', 'Б':'Базовая'},
    //   customFilterAndSearch: (term, rowData) => term.length === 0 || rowData.funding && _.intersection(term, rowData.funding).length > 0
    // },
    // {title: 'Код подраздела БК', field: 'bcCodes',
    //   cellStyle: {minWidth: '150px'},
    //   render: rowData => arrayStringRenderer(rowData.bcCodes),
    //   lookup: bcCodes,
    //   customFilterAndSearch: (term, rowData) => term.length === 0 || rowData.bcCodes && _.intersection(term, rowData.bcCodes).length > 0
    // },
    // {title: 'Ключевой показатель проекта', field: 'mainProjIndicator', lookup: { 1:'Показатель 1', 2:'Показатель 2', 3:'Показатель 3'}},
    {
      title: 'Статус',
      field: 'status',
      lookup: props.data?.type === 'policy' ? statusRuExtraWithPolicy : status_ru_extra,
      render: ({ status }) => <StatusBadge width={140} type={props.data?.type} status={status} />,
      filterComponent: ({ columnDef, onFilterChanged })=><FilterMultiSelect columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="status" />,
      customFilterAndSearch: (term, rowData) => {
      if(term.length==0) return true;
      return term.includes(rowData.status)
    },
    },
    {
      title: 'Наименование портфеля',
        field: 'portfolio',
      cellStyle: { minWidth: '300px' },
      render: ({ portfolio }) => (portfolio ? portfolio.name : null),
        lookup: portfFilterLookup,
      customFilterAndSearch: (term, rowData) => term.length === 0 || term.indexOf(rowData.portfolio?.name || 'Без портфеля') >= 0,
      filterComponent: ({ columnDef, onFilterChanged })=><FilterMultiSelect columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="portfolio" />,
    },
    // {title: 'Документы', field: 'docs'},
    ];
    
    // Фильтруем массив, чтобы убрать все не-объекты
    return allColumns.filter(column => column !== false && typeof column === 'object');
  }

  const materialTable =  useMemo(()=> <MaterialTable
  components={{
    Container: (props) => <div className="special-filter" {...props} />,
    FilterRow: (props) => (<MTableFilterRow {...props} />),
    Body: (props) => (
      <>
        <MTableBody {...props} />
        <div id="portal-root"></div>
        { getFooter(props) }
      </>
    ),
  }}
  title=""
  options={modifyDefaultOptions({
    pageSize: 10,
    pageSizeOptions: [10, 30, 50],
    headerStyle: {
      fontSize: 12,
      lineHeight: 1.3,
      padding: '8px 16px',
      zIndex: 0,

    },
    filterCellStyle: {
      padding: '8px 16px',
      position: 'sticky',
      top: 72,
      background: 'white',
    },
    rowStyle: (rowData) => ({
      // backgroundColor: rowData.tableData.id % 2 !== 0 ? '#FFF' : '#F7F7FF',
    }),
    filtering: true,
    sorting: true,
    search: false,
    paging: false,
    maxBodyHeight: 'calc(100vh - 436px)',
  })}
  icons={tableIcons}
  localization={localization}
  columns={getCols()}
  data={projects}
/>,[headFilterLookup,portfFilterLookup,letterFilterLookup,projects])

  useEffect(() => {
    getData(selYear?.value, selectedTags)
  }, [selYear, selectedTags]);
  // #endregion
  if (error) {
    return (
      <div style={{ color: 'red', background: 'white' }}>
        Ошибка:
        {error.message}
      </div>
    );
  }
  return (
    <div className="tab-subproject-register" style={{ color: 'initial' }}>
      <div style={styledContainer}>
        <div>
          <Select
            placeholder="Выберите год"
            defaultValue={[{ value: defaultYear, label: defaultYear }]}
            name="yearsSingle"
            options={modifiedYears}
            onChange={handleInputChange}
            value={selYear}
            isSearchable={false}
          />
        </div>
        {props.data?.type !== 'policy' && props.data?.type !== 'strategy' && (
          <div style={{ marginLeft: 16 }}>
            <NavLink to={`/${getPathType(props.data?.type)}/${props.data.id}/projs/new`}>
              <Button variant="primary">Добавить проект</Button>
            </NavLink>
          </div>
        )}
        { canExportPassports && props.data?.type === 'strategy' 
            && (
            <div style={{ marginLeft: 16 }}>
              <DownloadLink
                variant="outline-secondary"
                url={`/info/strategies/${stratId}/export?period=${selYear ? selYear.value : null}`}
                filename={`ЦП_${props.data.num}.xlsx`}
              >
                Выгрузить ЦП
              </DownloadLink>
            </div>
            )}
        {/* {canViewReport(projects) && <div style={{marginLeft: 16}}>
            <DownloadLink variant="outline-secondary"
                          url={`/reports/strategies/${props.data.id}/projresults/report/export?year=${selYear ? selYear.value : defaultYear}`}
                          filename={`Отчет по уник. результатам СП${props.data.id}.xlsx`}>
              Отчет по уникальным результатам
            </DownloadLink>
          </div>} */}
        <div className='ms-3'>
          <Select
            placeholder=''
            noOptionsMessage={(_) => 'Тегов не найдено'}
            options={tagOptions}
            value={
              selectedTags.length
                ? tagOptions.filter(opt => selectedTags.includes(opt.value))
                : { label: 'Все теги', value: null }
            }
            onChange={(opts) => setSelectedTags(opts.filter(opt => opt.value).map(opt => opt.value))}
            isMulti
            closeMenuOnSelect={false}
          />
        </div>
      </div>
      <LoadingTable loadingObject={{isLoaded}} spinnerStyle={{marginTop: '110px'}}>
        <div style={styledTable}>
          {materialTable}
        </div>
      </LoadingTable>
      {passportHistoryModal && <PassportVersionHistoryModal pass={passportHistoryModal} onClose={() => setPassportHistoryModal(null)} />}
      <ConfirmModal
        show={isModalDeleteProj.show}
        onClose={() => setIsModalDeleteProj({ show: false, value: null })}
        onConfirm={() => handleDeleteProj()}
        headerText="Удаление проекта"
        message="Проект будет удален, данное действие нельзя отменить. Удалить проект?"
      />
    </div>
  );
}

const styledTable = {
  maxWidth: '100%',
  marginTop: 32,
  position:'relative',
  zIndex:0
};
const styledContainer = {
  display: 'flex',
  zIndex: 11,
  position: 'relative',
  top: 84,
  marginTop: -90,
};
